import { useState, type ReactNode } from 'react';

import {
  Box,
  Button as MuiButton,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';

import BottomSheet from '@/designs-dev/device/BottomSheet';
import StatusBar from '@/designs-dev/device/StatusBar';
import HomeIndicator from '@/designs-dev/device/HomeIndicator';
import { useSystemChrome } from '@/designs-dev/device/SystemChromeContext';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import AppBar from '@/karehero-app/shell/AppBar';
import { PAGE_HEADER_TOP } from '@/karehero-app/shell/layout';
import {
  BAR,
  LEAVE,
} from '@/james-playground/aa-family-claim/AaFamilyClaim.content';

const SCREEN_PX = 3;

/** On the page for screen readers, off it for everyone else. */
export const visuallyHidden = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

/** The phone's home indicator on a phone; a little breathing room on a desktop. */
export const ScreenFloor = () =>
  useSystemChrome() ? <HomeIndicator /> : <Box sx={{ height: 16 }} />;

/**
 * The claim's own bar: where you are, the way back, and three segments for the
 * three things the claim asks of you. The segments replace the old four-tab
 * stepper, which counted an overview as a step and let you jump to a review of
 * nothing.
 */
export const ClaimBar = ({
  step,
  subtitle,
  onBack,
  onLeave,
  fill,
}: {
  /** Omit before the claim starts: the segments count work, and none is due yet. */
  step?: 0 | 1 | 2;
  subtitle?: string;
  onBack?: () => void;
  /**
   * The X. It asks first, and the asking is reassurance rather than a warning:
   * the answers are kept, so the sheet says that and offers the door. The
   * calculator's bar works the same way.
   */
  onLeave?: () => void;
  /** How far through the current step, 0 to 1, so a long step shows movement. */
  fill?: number;
}) => {
  const [leaving, setLeaving] = useState(false);
  return (
    <Box sx={{ flexShrink: 0 }}>
      <Typography component='h1' sx={visuallyHidden}>
        {BAR.title} claim
      </Typography>
      <AppBar
        title={BAR.title}
        subtitle={
          subtitle ?? (step === undefined ? undefined : BAR.steps[step])
        }
        onBack={onBack}
        align='left'
        divider={false}
        action={
          onLeave ? (
            <IconButton
              aria-label={LEAVE.label}
              size='small'
              onClick={() => setLeaving(true)}
            >
              <Icon icon={'xmark' as never} fontSize={18} />
            </IconButton>
          ) : undefined
        }
      />
      {step !== undefined && (
        <Stack
          aria-label={`Step ${step + 1} of ${BAR.steps.length}: ${BAR.steps[step]}`}
          role='img'
          sx={{ px: SCREEN_PX, pb: 1.5 }}
          direction='row'
          spacing={0.5}
        >
          {BAR.steps.map((label, i) => (
            <Box
              key={label}
              sx={{
                flex: 1,
                height: 4,
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: i < step ? 'primary.main' : 'custom.neutral.200',
              }}
            >
              {i === step && (
                <Box
                  sx={{
                    height: '100%',
                    width: `${Math.round((fill ?? 1) * 100)}%`,
                    minWidth: 8,
                    bgcolor: 'primary.main',
                    transition: 'width 400ms ease',
                  }}
                />
              )}
            </Box>
          ))}
        </Stack>
      )}
      <Divider />
      {leaving && onLeave && (
        <BottomSheet
          variant='hug'
          title={LEAVE.title}
          onClose={() => setLeaving(false)}
          footer={(close) => (
            <Stack spacing={2}>
              <MuiButton
                variant='contained'
                size='large'
                fullWidth
                onClick={() => {
                  close();
                  onLeave();
                }}
              >
                {LEAVE.confirm}
              </MuiButton>
              <MuiButton
                variant='outlined'
                size='large'
                fullWidth
                onClick={close}
              >
                {LEAVE.stay}
              </MuiButton>
            </Stack>
          )}
        >
          <Typography variant='body1'>{LEAVE.body}</Typography>
        </BottomSheet>
      )}
    </Box>
  );
};

/**
 * One step of the claim: the bar, a page header, scrolling content and a footer
 * that stays put. StepScreen's layout with an open footer slot, in place of a fixed
 * primary and Back, because the claim's steps end in different actions.
 */
const ClaimScreen = ({
  bar,
  title,
  description,
  eyebrow,
  children,
  footer,
  overlay,
}: {
  bar?: ReactNode;
  title?: string;
  description?: string;
  /** A short line above the title. */
  eyebrow?: string;
  children?: ReactNode;
  footer?: ReactNode;
  /** Floats over the step, positioned against the screen. */
  overlay?: ReactNode;
}) => (
  <ScreenRoot
    relative
    // On a short screen, or with text made large, a pinned bar and footer
    // leave a few lines for the step itself. There the whole step scrolls as
    // one, footer included, so the content gets the height.
    sx={{
      '@media (max-height: 560px)': {
        overflow: 'auto',
        '& > main': { flex: 'none', overflow: 'visible' },
      },
    }}
  >
    <StatusBar />
    {bar}
    <Box
      component='main'
      tabIndex={0}
      aria-label={title ?? eyebrow}
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        px: SCREEN_PX,
        pb: 3,
        '&:focus-visible': { outline: 'none' },
      }}
    >
      <Stack sx={{ pt: PAGE_HEADER_TOP }} spacing={3}>
        {(title || eyebrow) && (
          <Stack spacing={1}>
            {eyebrow && (
              <Typography sx={{ color: 'text.secondary' }} variant='overline'>
                {eyebrow}
              </Typography>
            )}
            {title && (
              <PageHeader
                title={title}
                description={description}
                variant='h2'
              />
            )}
          </Stack>
        )}
        {children}
      </Stack>
    </Box>
    {overlay}
    {footer && (
      <Box
        sx={{
          px: SCREEN_PX,
          pt: 1.5,
          pb: 0.5,
          bgcolor: 'background.default',
          borderTop: 1,
          borderColor: 'custom.neutral.200',
        }}
      >
        <Stack spacing={1.5}>{footer}</Stack>
      </Box>
    )}
    <ScreenFloor />
  </ScreenRoot>
);

export default ClaimScreen;
